import { Injectable, signal, computed, inject } from '@angular/core';
import { StoreDataService } from './store-data';
import { ToastService } from './toast.service';
import { toSlug } from '../utils/slug';

export interface CartItem {
  slug:          string;
  name:          string;
  category:      string;
  price:         number;
  originalPrice: number;
  img:           string;
  size:          string;
  qty:           number;
}

export interface DeliveryAddress {
  flat: string; street: string; landmark: string;
  pincode: string; city: string; country: string; state: string;
  name: string; phone: string;
  saveAs: 'Home' | 'Work' | 'Other'; isDefault: boolean;
}

@Injectable({ providedIn: 'root' })
export class CartService {
  private store = inject(StoreDataService);
  private toast = inject(ToastService);
  private _items = signal<CartItem[]>([]);

  // Selected delivery address (set on the ADDRESS step, read by cart + checkout)
  readonly deliveryAddress = signal<DeliveryAddress | null>(null);

  readonly items    = this._items.asReadonly();
  readonly count    = computed(() => this._items().reduce((s, i) => s + i.qty, 0));
  readonly subtotal = computed(() => this._items().reduce((s, i) => s + i.price * i.qty, 0));

  constructor() {
    // Sample items so the filled-cart UI is visible for review (clear() to empty it).
    const sizes = ['M', '32', 'L'];
    this._items.set(
      this.store.newArrivals.slice(0, 3).map((p, i) => ({
        slug: toSlug(p.name), name: p.name, category: p.category,
        price: p.price, originalPrice: p.originalPrice, img: p.img, size: sizes[i], qty: i === 2 ? 2 : 1,
      })),
    );
  }

  updateSize(slug: string, oldSize: string, newSize: string) {
    this._items.update(list =>
      list.map(i => (i.slug === slug && i.size === oldSize ? { ...i, size: newSize } : i)),
    );
  }

  add(item: Omit<CartItem, 'qty'>, qty = 1) {
    this._items.update(list => {
      const idx = list.findIndex(i => i.slug === item.slug && i.size === item.size);
      if (idx >= 0) {
        const copy = [...list];
        copy[idx] = { ...copy[idx], qty: copy[idx].qty + qty };
        return copy;
      }
      return [...list, { ...item, qty }];
    });
    this.toast.show('Product added to your cart successfully.');
  }

  updateQty(slug: string, size: string, qty: number) {
    if (qty <= 0) { this.remove(slug, size); return; }
    this._items.update(list =>
      list.map(i => i.slug === slug && i.size === size ? { ...i, qty } : i)
    );
  }

  remove(slug: string, size: string) {
    this._items.update(list => list.filter(i => !(i.slug === slug && i.size === size)));
  }

  clear() { this._items.set([]); }
}
